import React from 'react'

import { Heading1, Link, TextAlign, Space } from '@gousto-internal/citrus-react'
import { useDecision } from '@optimizely/react-sdk'
import { Heading, LayoutPageWrapper } from 'goustouicomponents'
import Helmet from 'react-helmet'

import { getStaticAssetURL } from '@library/static-assets'

import policy from 'config/policy'

import { CookiesPolicyVariant } from './CookiePolicyVariant/CookiesPolicyVariant'

import css from './PrivacyStatement.css'

const PrivacyStatement = () => {
  const [decision] = useDecision('fef_ofx_cookie_banner_test')
  const isVariant = decision.variationKey === 't1'

  return (
    <LayoutPageWrapper>
      <Helmet title={policy.head.title} meta={policy.head.meta} />

      <section className={css.mainSection} id="privacy-policy">
        <Heading1 textAlign={TextAlign.Center}>Privacy Policy</Heading1>
        <Space size={4} />
        <p>
          Want to read all about how we handle your data? Check out the{' '}
          <Link href={`${getStaticAssetURL('/pdfs/Gousto_Privacy_Policy_V3.pdf')}`} target="_blank">
            Gousto Privacy Policy
          </Link>{' '}
          for everything you need to know.
        </p>
      </section>

      {isVariant ? (
        <CookiesPolicyVariant />
      ) : (
        <section className={css.mainSection} id="cookies-policy-control">
          <Heading1 textAlign={TextAlign.Center}>Cookies Policy</Heading1>
          <p>Effective Date: 26/10/2020 (Version 2.0)</p>
          <ol className={css.orderedList}>
            <li className={css.itemList}>
              <Heading type="h2" size="fontStyleM">
                What about cookies?
              </Heading>
              <ol className={css.orderedList}>
                <li className={css.itemList}>
                  Our website uses cookies to distinguish you from other users of our website. This
                  helps us to provide you with a good experience when you browse our website and
                  also allows us to improve our website. A cookie is a small text file that is
                  downloaded onto &apos;terminal equipment&apos; (eg a computer or smartphone) when
                  the user accesses a website. It allows the website to recognise that user&apos;s
                  device and store some information about the user&apos;s preferences or past
                  actions.
                </li>
                <li className={css.itemList}>
                  You can consent to, or block the use of, specific Cookies by activating the
                  relevant settings in your browser or in the case of third party cookies - by
                  contacting the third parties directly. We don&apos;t have access to the cookies
                  which third parties place on the website; other than allowing them to be served.
                  If you would like more information about these practices and to know your choices
                  about not having this information used by certain of these companies, please visit
                  www.youronlinechoices.com and www.networkadvertising.org/choices. In order to use
                  some parts of our site, you will need to consent to Cookies. If you choose to
                  withhold consent, or subsequently block cookies, some aspects of our site may not
                  work properly and you may not be able to access all or part of the site.
                </li>
                <li className={css.itemList}>
                  Generally, there are the following categories of cookies:
                  <ol className={css.orderedList}>
                    <li className={css.itemList}>
                      Strictly Necessary cookies: these cookies are essential for the operation of
                      the website. They often improve the usability of the website for you by
                      allowing you to proceed through many pages of the website quickly and easily
                      without having to authenticate or reprocess each new area you visit. For
                      example, the session cookie remembers your shopping cart selection so will
                      have the items you selected when you are ready to check out.
                    </li>
                    <li className={css.itemList}>
                      Performance and analytics cookies: these cookies analyse how you use the site
                      and to monitor performance so that we can provide a high quality experience to
                      you and quickly identify issues that may arise. We also use cookies to
                      understand, improve, develop and research products, features and services, to
                      collect usage, viewing, logs, and technical information about you (such as
                      event data), and to create logs and record when you access our site from
                      different devices, such as your work computer or your mobile device.This
                      information helps us to improve the way our website works, for example by
                      making sure users find what they need easily. We predominantly use Google
                      Analytics (“GA”) cookies to conduct analytics. Click&nbsp;
                      <a
                        href="https://policies.google.com/technologies/types?gl=uk"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here
                      </a>
                      &nbsp;for more information about GA cookies. Click&nbsp;
                      <a
                        href="https://tools.google.com/dlpage/gaoptout"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here
                      </a>
                      &nbsp;to opt out of GA cookies.
                    </li>
                    <li className={css.itemList}>
                      Targeting cookies: these cookies are used to serve users with content and
                      adverts, and to collect information about your browsing habits and usage of
                      the website to make adverts more relevant to you based on your visits to this
                      and other websites. We may use remarketing technologies to enable third
                      parties to display relevant and personalised ads to users through their
                      networks. They are also used to limit the number of times users see an advert
                      as well as help measure the effectiveness of an advertising campaign.
                    </li>
                    <li className={css.itemList}>
                      Social media cookies: We may use various social media and other third party
                      features on our website including the&nbsp;
                      <a
                        href="https://www.facebook.com/help/186325668085084/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        like
                      </a>
                      &nbsp;button from&nbsp;
                      <a
                        href="http://www.facebook.com/help/cookies"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Facebook
                      </a>
                      , follow button from&nbsp;
                      <a
                        href="https://twitter.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Twitter
                      </a>
                      ),&nbsp;
                      <a
                        href="https://support.google.com/plus/answer/1319578?hl=en-GB"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Plus +1
                      </a>
                      &nbsp;button from&nbsp;
                      <a
                        href="https://www.google.co.uk/policies/technologies/types/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Google
                      </a>
                      , Pin It button from&nbsp;
                      <a
                        href="http://about.pinterest.com/en/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Pinterest
                      </a>
                      , share / follow button from&nbsp;
                      <a
                        href="https://www.linkedin.com/legal/cookie-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LinkedIn
                      </a>
                      , embed button from&nbsp;
                      <a
                        href="https://www.google.co.uk/policies/technologies/types/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        YouTube
                      </a>
                      , These features may involve the relevant companies using cookies or linking
                      your visit with cookies previously placed by them on your computer in order
                      for them to collect information relating to your visit to our website or your
                      interaction with their services or otherwise. Click the links shown above for
                      further information about the specific features and those companies&apos;
                      cookie / privacy practices generally as well as to opt out where this is
                      possible.
                    </li>
                  </ol>
                </li>
                <li className={css.itemList}>
                  We hope our use of cookies is clear and please be aware you can set your browser
                  to reject cookies or you can delete them yourself if you wish. For further
                  information on cookies, including how to use your browser to block them and how to
                  delete cookies already stored on your device, visit:&nbsp;
                  <a
                    href="https://www.allaboutcookies.org"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    www.allaboutcookies.org
                  </a>
                  .
                </li>
              </ol>
            </li>
          </ol>
        </section>
      )}
    </LayoutPageWrapper>
  )
}

export { PrivacyStatement }
